import React, {useEffect, useState} from 'react';
import ImageUpload from "../Common/File/ImageUpload";
import {METADATA_VALUE, PRODUCT_STATUS, VARIANT} from "../../constants";
import Input from "../Common/Input";
import Editor from "../Common/Editor";
import Button from "../Common/Button";
import productStore from "../../store/productStore";
import {helperUpload} from "../../network/api";
import {checkUpload, cleanNumber, formatNumberWithDots, scrollToTop} from "../../utils";
import NestedSelect from "../Common/Select/NestedSelect";
import appStore from "../../store/appStore";
import cn from "classnames";
import IconText from "../Common/Text/IconText";
import IMG from "../../constants/image";
import ImageBasic from "../Common/Image/ImageBasic";
import CheckboxInput from "../Common/Input/CheckboxInput";
import {clsx} from "clsx";
import {isMobile} from "react-device-detect";
import {isEmpty} from "lodash";

const ProductForm = ({
                       editing = false,
                       isAdding = false,
                       creating,
                       productDetail,
                       buttonSubmit,
                       actionSubmit,
                       setDeletedVariantIds
                     }) => {
  const {categoryData, getProductCate} = productStore() || {};
  const {setLoading} = appStore() || {};
  // State to hold form data
  const [categories, setCategories] = useState([])
  const [thumbIndex, setThumbnailIndex] = useState(0)
  const [activeCategory, setActiveCategory] = useState(0)
  const [temporaryRetails, setTemporaryRetails] = useState([]);
  const prevNotAllowSale = React.useRef(false);


  const {
    id,
    img_objects: imageFiles = [],
    category: categoryIds = [],
    title = '',
    metadata,
    thumbnail,
    retail_variants,
    enable_retail,
    enable_wholesale,
    wholesale
  } = productDetail || {};
  const {product_detail} = metadata || {}
  const [htmlString, setHtmlString] = useState('')
  const [images, setImages] = useState(imageFiles)
  const [formData, setFormData] = useState({
    isRetail: !editing && !isEmpty(retail_variants),
    isWholesale: !editing && !isEmpty(wholesale),
    retails: [
      {
        unit: '',
        variant_name: '',
        price: '',
        weight: ''
      }
    ],
    productName: title || '',
    categoryId: categoryIds?.[0]?.id || '',
    minPrice: wholesale?.min_price,
    maxPrice: wholesale?.max_price,
    minOrderQty: wholesale?.min_order_qty,
    unitOfMeasure: wholesale?.unit_of_measure,
    imagesUploaded: null,
  });

  const [errors, setErrors] = useState({});

  const addRetails = (variantId) => {
    setFormData(prev => ({
      ...prev,
      retails: [
        ...prev.retails,
        {
          variant_name: prev.retails.find(v => v?.id === variantId)?.variant_name || '',
          unit: prev.retails.find(v => v.id === variantId)?.unit || '',
          price: '',
          weight: ''
        }
      ]
    }))
  }
  const updateRetails = (index, field, value) => {
    //update retail price after format currency
    if (field === 'price' || field === 'weight') {
      value = cleanNumber(value)
    }
    setFormData(prev => ({
      ...prev,
      retails: prev.retails.map((retail, idx) =>
        idx === index ? {...retail, [field]: value} : retail
      )
    }))
  }

  const deleteVariant = (index) => {
    if (formData.retails.length <= 1) return // Prevent deleting the last retail

    const variantToDelete = formData.retails[index];

    // Store the deleted variant ID if it exists
    if (variantToDelete && variantToDelete.id) {
      setDeletedVariantIds(prev => [...prev, variantToDelete.id]);
    }

    // Remove the variant at the specified index
    setFormData(prev => ({
      ...prev,
      retails: prev.retails.filter((_, idx) => idx !== index)
    }))

    // Clear any errors associated with the deleted retail
    const newErrors = {...errors}
    const variantId = variantToDelete?.id;
    if (variantId) {
      Object.keys(newErrors).forEach(key => {
        if (key.includes(variantId)) {
          delete newErrors[key]
        }
      })
    }
    setErrors(newErrors)
  }

  useEffect(() => {
    getProductCate();
  }, [])

  useEffect(() => {
    const index = (images || []).findIndex(img => img.url === thumbnail)
    setThumbnailIndex(index < 0 ? 0 : index);
  }, [thumbnail, images])

  useEffect(() => {
    // Initialize retails array with data from retail_variants if available
    const initialRetails = retail_variants?.length > 0
      ? retail_variants.map(variant => ({
        id: variant.id,
        variant_name: variant.variant_name || '',
        unit: variant.unit || '',
        price: variant.price?.toString() || '',
        weight: variant.weight?.toString() || ''
      }))
      : [{
        variant_name: '',
        unit: '',
        price: '',
        weight: ''
      }];
    const hasRetailPrices =
      Array.isArray(retail_variants) &&
      retail_variants.some((v) => {
        const p = Number(cleanNumber(String(v?.price ?? '')));
        return !isNaN(p) && p > 0;
      });
    const minWholesale = Number(cleanNumber(String(wholesale?.min_price ?? '')));
    const maxWholesale = Number(cleanNumber(String(wholesale?.max_price ?? '')));
    const hasWholesalePrices =
      !!wholesale &&
      !isNaN(minWholesale) &&
      !isNaN(maxWholesale) &&
      minWholesale > 0 &&
      maxWholesale > 0;

    setFormData({
      isRetail: editing ? Boolean(enable_retail && hasRetailPrices) : true,
      isWholesale: editing ? Boolean(enable_wholesale && hasWholesalePrices) : true,
      retails: initialRetails,
      productName: title || '',
      categoryId: categoryIds?.[0]?.id || '',
      minPrice: wholesale?.min_price,
      maxPrice: wholesale?.max_price,
      minOrderQty: wholesale?.min_order_qty || '',
      unitOfMeasure: wholesale?.unit_of_measure || '',
      imagesUploaded: null,
    })

    setHtmlString(product_detail?.htmlString || '')
    setImages(imageFiles)
  }, [productDetail])

  useEffect(() => {
    setCategories(categoryData?.product_categories || [])
  }, [categoryData])

  useEffect(() => {
    const findCategoryRecursive = (items, targetId) => {
      for (const item of items) {
        if (item.id === targetId) return item;
        if (item.category_children?.length > 0) {
          const found = findCategoryRecursive(item.category_children, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    if (formData?.categoryId && categories?.length > 0) {
      const cate = findCategoryRecursive(categories, formData.categoryId);
      setActiveCategory(cate);
    }
  }, [formData?.categoryId, categories])

  const notAllowSale = activeCategory?.metadata?.not_allow_sale === METADATA_VALUE.YES

  useEffect(() => {
    if (notAllowSale) {
      if (!formData.isRetail && !formData.isWholesale && formData.retails.length === 0) return;

      const retails = formData.retails;
      const existingVariantIds = retails.filter(r => r.id).map(r => r.id);

      if (existingVariantIds.length > 0 && setDeletedVariantIds) {
        setDeletedVariantIds(prev => {
          const newIds = existingVariantIds.filter(id => !prev.includes(id));
          return newIds.length ? [...prev, ...newIds] : prev;
        });
      }
      if (retails.length > 0) {
        setTemporaryRetails(retails);
      }

      setFormData(prev => ({
        ...prev,
        isRetail: false,
        isWholesale: false,
        retails: []
      }));
    } else {
      // Restore when switching from notAllowSale to allowed
      if (prevNotAllowSale.current) {
        setFormData(prev => ({
          ...prev,
          isRetail: true,
          isWholesale: true,
          retails: temporaryRetails.length > 0 ? temporaryRetails : [{
            variant_name: '',
            unit: '',
            price: '',
            weight: ''
          }]
        }));
        setTemporaryRetails([]);
      }
    }
    prevNotAllowSale.current = notAllowSale;
  }, [notAllowSale, setDeletedVariantIds]);

  const handleChangeEditor = (editorData) => {
    setHtmlString(editorData);
  }

  const handleInputChange = (e) => {
    const {name, value} = e.target || {};
    setFormData({...formData, [name]: value});
    setErrors({...errors, [name]: ''});
  };

  const handleImageUpload = (files) => {
    setImages(files)
    setErrors(prev => ({...prev, images: ''}));
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    let firstErrorId = '';

    if (!formData.productName) {
      newErrors.productName = 'Tên sản phẩm là bắt buộc';
      if (!firstErrorId) firstErrorId = 'productName';
    }
    if (!formData.categoryId) {
      newErrors.categoryId = 'Danh mục là bắt buộc';
      if (!firstErrorId) firstErrorId = 'categoryId';
    }

    if (!images || images.length === 0) {
      newErrors.images = 'Vui lòng tải lên ít nhất 1 hình ảnh';
      if (!firstErrorId) firstErrorId = 'images';
    }

    if (!notAllowSale && !formData.isRetail && !formData.isWholesale) {
      newErrors.saleType = 'Vui lòng chọn ít nhất 1 lựa chọn bán lẻ hoặc bán sỉ';
      if (!firstErrorId) firstErrorId = 'saleType';
    }
    formData.retails.forEach((retail, _index) => {
      if (formData.isRetail) {
        if (!retail.variant_name.trim()) {
          newErrors[`variant_${retail.id}_productType`] = 'Vui lòng nhập loại sản phẩm'
          if (!firstErrorId) firstErrorId = `variant_name-${retail.id}`;
        }

        if (!retail.unit.trim()) {
          newErrors[`variant_${retail.id}_unit`] = 'Vui lòng nhập đơn vị'
          if (!firstErrorId) firstErrorId = `unit-${retail.id}`;
        }
        if (!retail.price) {
          newErrors[`variant_${retail.id}_retailPrice`] = 'Vui lòng nhập giá bán lẻ'
          if (!firstErrorId) firstErrorId = `price-${retail.id}`;
        } else {
          const price = Number(retail.price)
          if (isNaN(price)) {
            newErrors[`variant_${retail.id}_retailPrice`] = 'Giá bán lẻ phải là số'
            if (!firstErrorId) firstErrorId = `price-${retail.id}`;
          } else if (price <= 200) {
            newErrors[`variant_${retail.id}_retailPrice`] = 'Giá bán lẻ phải lớn hơn 200 VND'
            if (!firstErrorId) firstErrorId = `price-${retail.id}`;
          }
        }
        if (!retail.weight || (typeof retail.weight === 'string' && !retail.weight.trim())) {
          newErrors[`variant_${retail.id}_packageWeight`] = 'Vui lòng nhập trọng lượng đóng gói'
          if (!firstErrorId) firstErrorId = `weight-${retail.id}`;
        } else if (isNaN(Number(retail.weight))) {
          newErrors[`variant_${retail.id}_packageWeight`] = 'Trọng lượng phải là số'
          if (!firstErrorId) firstErrorId = `weight-${retail.id}`;
        }
      }
    })

    if (formData.isWholesale && !notAllowSale) {
      const minP = Number(cleanNumber(formData?.minPrice ?? ''));
      const maxP = Number(cleanNumber(formData?.maxPrice ?? ''));

      if (!minP || isNaN(minP)) {
        newErrors.minPrice = 'Vui lòng nhập giá tối thiểu';
        if (!firstErrorId) firstErrorId = 'minPrice';
      }
      if (!maxP || isNaN(maxP)) {
        newErrors.maxPrice = 'Vui lòng nhập giá tối đa';
        if (!firstErrorId) firstErrorId = 'maxPrice';
      }
      if (
        minP &&
        maxP &&
        !isNaN(minP) &&
        !isNaN(maxP) &&
        minP > maxP
      ) {
        newErrors.maxPrice = 'Giá tối thiểu phải nhỏ hơn giá tối đa';
        if (!firstErrorId) firstErrorId = 'maxPrice';
      }

      const moq = Number(cleanNumber(formData?.minOrderQty ?? ''));
      if (moq > 0 && !String(formData?.unitOfMeasure || '').trim()) {
        newErrors.unitOfMeasure = 'Vui lòng nhập đơn vị';
        if (!firstErrorId) firstErrorId = 'unitOfMeasure';
      } else if (!moq && String(formData?.unitOfMeasure || '').trim()) {
        newErrors.minOrderQty = 'Vui lòng nhập số lượng đặt hàng ít nhất';
        if (!firstErrorId) firstErrorId = 'minOrderQty';
      }
    }

    return {...newErrors, firstErrorId};
  };

  const handleSetThumbnail = (allImages, uploadedImages, index) => {
    if (allImages.length - uploadedImages.length <= index) {
      return uploadedImages?.[index - allImages.length + uploadedImages.length].url
    } else {
      return allImages[index].url
    }
  }

  const handleSubmit = async (e) => {

    e.preventDefault();
    const newFormData = {...formData, id};
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 1) {
      setErrors(formErrors); // Set validation errors
      if (formErrors.firstErrorId) {
        const errorEl = document.getElementById(formErrors.firstErrorId);
        if (errorEl) {
          errorEl.scrollIntoView({behavior: "smooth", block: "center", inline: "nearest"});
        }
      }
      return;
    }
    setLoading(true);
    scrollToTop();
    const needUploadFiles = checkUpload(images);
    if (needUploadFiles?.length > 0) {
      const uploadData = await helperUpload({files: needUploadFiles});
      if (uploadData?.success) {
        newFormData.imagesUploaded = (((images || []).filter(img => img?.id)).map(img => img.id)).concat(uploadData?.data?.map(item => item?.key));
        newFormData.thumbnail = handleSetThumbnail(images, uploadData?.data, thumbIndex);
      }
    } else {
      newFormData.imagesUploaded = images?.map((item) => item?.id);
      if (images && images.length > 0 && thumbIndex !== undefined && images[thumbIndex]) {
        newFormData.thumbnail = images[thumbIndex].url;
      } else {
        newFormData.thumbnail = null;
        console.warn('No valid thumbnail found');
      }
    }

    newFormData.productDetail = {...product_detail, htmlString};
    if (actionSubmit) {
      const submitRes = await actionSubmit(newFormData);
      if (creating && submitRes?.success) {
        setFormData({
          isRetail: true,
          isWholesale: true,
          retails: [
            {
              variant_name: '',
              unit: '',
              price: '',
              weight: ''
            }
          ],
          productName: '',
          categoryId: '',
          minPrice: '',
          maxPrice: '',
          minOrderQty: '',
          unitOfMeasure: '',
          imagesUploaded: null,
        });
        setHtmlString('');
        setImages([]);
      }
    }
  };
  const handleChangeCheckbox = (e) => {
    if (notAllowSale) return;

    // Clear sale-type error once user interacts again
    setErrors(prev => ({...prev, saleType: ''}));

    const newValue = e?.target?.value;

    if (!newValue && formData.isRetail) {
      // When unchecking, store current retails and mark existing variants for deletion
      const existingVariantIds = formData.retails
        .filter(retail => retail.id) // Only include variants with IDs (existing ones)
        .map(retail => retail.id);

      // Only set deletedVariantIds if there are existing variants
      if (existingVariantIds.length > 0) {
        setDeletedVariantIds(prev => {
          // Avoid adding duplicate variant IDs
          const newDeletedIds = existingVariantIds.filter(id => !prev.includes(id));
          return newDeletedIds.length > 0 ? [...prev, ...newDeletedIds] : prev;
        });
      }

      // Store current retails in temporary storage
      setTemporaryRetails(formData.retails);

      // Set retails to empty array
      setFormData({
        ...formData,
        isRetail: false,
        retails: []
      });
    } else if (newValue && !formData.isRetail) {
      // When checking back, restore temporary retails or create a default variant
      const restoreRetails = temporaryRetails.length > 0
        ? temporaryRetails
        : [{
          variant_name: '',
          unit: '',
          price: '',
          weight: ''
        }];

      // Remove restored variant IDs from deletedVariantIds
      const restoredVariantIds = restoreRetails
        .filter(retail => retail.id)
        .map(retail => retail.id);

      if (restoredVariantIds.length > 0) {
        setDeletedVariantIds(prev =>
          prev.filter(id => !restoredVariantIds.includes(id))
        );
      }

      setFormData({
        ...formData,
        isRetail: true,
        retails: restoreRetails
      });

      // Clear temporary storage
      setTemporaryRetails([]);
    } else {
      // Simply update isRetail value if no state change is needed
      setFormData({...formData, isRetail: newValue});
    }
  }

  return (
    <form className='flex flex-col gap-4 md:gap-5 justify-center bg-[#EDEDED] md:bg-[#FAFAFC] pb-[100px] md:pb-0'
          onSubmit={handleSubmit}>
      <div className='product-section !pb-6'>
        {
          productDetail && productDetail?.status === PRODUCT_STATUS.REJECTED && (
            <IconText
              className='w-fit'
              icon={IMG.icon.exclamation}
              iconClass='mt-[-14px]'
              textChild={(
                <div>
                  <p className='text-red-600 mb-[5px]'>Sản phẩm đang bị từ chối</p>
                  <p className='text-[#9E9E9E]'>{`Lý do: ${metadata?.rejected_reason || ''}`}</p>
                </div>
              )}
            />
          )
        }
        <Input
          required
          title='Tên sản phẩm'
          placeholder='Nhập tên sản phẩm'
          className='text-sm'
          id="productName"
          name="productName"
          setError={setErrors}
          value={formData.productName}
          onChange={handleInputChange}
          error={errors.productName}
        />

        <NestedSelect
          required
          data={categories}
          title="Danh mục"
          placeholder="Vui lòng chọn"
          id="categoryId"
          name="categoryId"
          value={formData.categoryId}
          onChange={handleInputChange}
          viewOnly={!editing && !isAdding}
          error={errors.categoryId}
        />
      </div>
      <div id='saleType' className={cn('product-section text-sm', notAllowSale && 'text-gray-400')}
           style={notAllowSale ? {backgroundColor: '#ccced129'} : {}}>
        <div className='md:flex md:items-center md:justify-center md:space-x-2 text-sm font-bold'>
          <ImageBasic className='hidden md:block w-5' src={IMG.icon.errorOutlineBlack}/>
          <div>Vui lòng chọn các lựa chọn bên dưới theo mục đích bán hàng của bạn <span
            className='text-[#E4626F]'>*</span></div>
          <div className="md:hidden text-xs font-normal italic mt-2">
            (Nếu bạn chọn cả 2 lựa chọn, sản phẩm này sẽ được dành cho bán sỉ và lẻ)
          </div>
        </div>
        {errors.saleType && !notAllowSale && (
          <div className='text-xs text-[#FF0019] mt-2 flex items-center gap-1'>
            <img src={IMG.icon.errorOutline} alt="errorOutline"/>
            <p className='text-[#E4626F] text-xs'>{errors.saleType}</p>
          </div>
        )}
        <div className="hidden md:block text-sm md:space-y-2">
          <div className='flex gap-6'>
            <div className="flex items-center font-bold">
              <CheckboxInput
                className='checked:bg-[#F3F8F6] border-[#A3CBBB] bg-[#F3F8F6]'
                color='text-[#197D55]'
                id="retail"
                name="retail"
                checked={formData.isRetail}
                disabled={notAllowSale}
                onChange={(e) => handleChangeCheckbox(e)}
              />
              <label className={cn(notAllowSale && 'cursor-not-allowed')} htmlFor="retail">Dành cho bán lẻ</label>
            </div>
            <div className="flex items-center font-bold">
              <CheckboxInput
                className='checked:bg-[#F3F8F6] border-[#A3CBBB] bg-[#F3F8F6]'
                color='text-[#197D55]'
                name="wholesale"
                id="wholesale"
                checked={formData.isWholesale}
                disabled={notAllowSale}
                onChange={({target}) => {
                  if (notAllowSale) return;
                  setFormData({...formData, isWholesale: target?.value});
                  setErrors(prev => ({...prev, saleType: ''}));
                }}
              />
              <label className={cn(notAllowSale && 'cursor-not-allowed')} htmlFor="wholesale">Dành cho bán sỉ</label>
            </div>
          </div>
          <div className="text-xs italic">
            (Nếu bạn chọn cả 2 lựa chọn, sản phẩm này sẽ được dành cho bán sỉ và lẻ)
          </div>
          {
            notAllowSale && (
              <IconText
                className='w-fit text-yellow-500 mb-2'
                text='Ngành hàng này không hỗ trợ nhập giá và số lượng!'
              />
            )
          }
        </div>
        <div
          className={clsx(!formData.isRetail && !isMobile && 'hidden', "text-sm rounded-xl md:rounded-none overflow-hidden")}>
          <div className="md:hidden flex items-center font-bold px-4 py-4 bg-[#FAFAFC]">
            <CheckboxInput
              className='checked:bg-[#F3F8F6] border-[#A3CBBB] bg-[#F3F8F6]'
              color='text-[#197D55]'
              id="retail"
              name="retail"
              checked={formData.isRetail}
              disabled={notAllowSale}
              onChange={(e) => handleChangeCheckbox(e)}
            />
            <label className={cn(notAllowSale && 'cursor-not-allowed')} htmlFor="retail">Dành cho bán lẻ</label>
          </div>
          {formData.retails.map((retail, index) => (
            <div key={index}
                 className={clsx(
                   !formData.isRetail && 'hidden',
                   index > 0 ? 'rounded-xl md:mt-5' : 'rounded-b-xl',
                   "md:rounded-xl md:space-y-5 bg-[#FAFAFC] px-3 pb-5 md:pt-3 space-y-3")}>
              {index > 0 && <div className='flex justify-between items-center mt-4 md:mt-0 pt-4 md:pt-0'>
                <div className="text-sm text-[#197D55] font-bold rounded">
                  Biến thể {index + 1}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => deleteVariant(index)}
                  className="text-gray-500 hover:text-[#E4626F] !p-0"
                >
                  <ImageBasic className='w-6 h-6' src={IMG.icon.close}/>
                </Button>
              </div>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-5">
                <div className="space-y-1">
                  <label htmlFor={`variant_name-${retail.id}`}>Loại sản phẩm <span
                    className='text-[#E4626F]'>*</span></label>
                  <Input
                    id={`variant_name-${retail.id}`}
                    className='bg-white'
                    placeholder="VD: khô 1 nắng, 2 nắng..."
                    value={retail.variant_name}
                    onChange={(e) => updateRetails(index, 'variant_name', e.target.value)}
                    error={errors[`variant_${retail.id}_productType`]}
                    setError={setErrors}
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor={`unit-${retail.id}`}>Đơn vị <span
                    className='text-[#E4626F]'>*</span></label>
                  <Input
                    id={`unit-${retail.id}`}
                    className='bg-white'
                    placeholder="VD: kg, con, bịch..."
                    value={retail.unit}
                    onChange={(e) => updateRetails(index, 'unit', e.target.value)}
                    error={errors[`variant_${retail.id}_unit`]}
                    setError={setErrors}
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor={`price-${retail?.id}`}>Giá bán lẻ <span
                    className='text-[#E4626F]'>*</span></label>
                  <div className="relative">
                    <Input
                      id={`price-${retail?.id}`}
                      className='bg-white'
                      value={formatNumberWithDots(retail.price)}
                      onChange={(e) => updateRetails(index, 'price', e.target.value)}
                      error={errors[`variant_${retail?.id}_retailPrice`]}
                      setError={setErrors}
                    />
                    <span
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">đ</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <label htmlFor={`weight-${retail?.id}`}>Trọng lượng đóng gói <span
                    className='text-[#E4626F]'>*</span></label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        className='bg-white'
                        id={`weight-${retail?.id}`}
                        value={retail.weight}
                        onChange={(e) => updateRetails(index, 'weight', e.target.value)}
                        placeholder="Nhập trọng lượng"
                        error={errors[`variant_${retail?.id}_packageWeight`]}
                        setError={setErrors}
                      />
                      <span
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">kg</span>
                    </div>
                    {formData.retails.length - 1 === index && <Button
                      type="button"
                      retail={formData.retails.length === 10 ? VARIANT.SECONDARY : VARIANT.PRIMARY}
                      disabled={formData.retails.length === 10}
                      onClick={() => addRetails(retail?.id)}
                      className="hidden md:flex h-[38px] w-[38px] rounded-lg text-black text-2xl"
                    >
                      +
                    </Button>}
                  </div>
                </div>
              </div>
              {formData.retails.length - 1 === index && <Button
                type="button"
                variant={formData.retails.length === 10 ? VARIANT.SECONDARY : VARIANT.PRIMARY}
                disabled={formData.retails.length === 10}
                onClick={() => addRetails(retail?.id)}
                className="md:hidden w-full h-[38px] !mt-6 rounded-2xl text-black text-base"
              >
                + Thêm thuộc tính
              </Button>}
            </div>
          ))}
        </div>
        <div
          className={clsx(!formData.isWholesale && !isMobile ? 'hidden' : '',
            'bg-[#FAFAFC] rounded-xl px-[10px] md:px-3 md:pt-3')}>
          <div className="md:hidden flex items-center px-1 py-4 font-bold">
            <CheckboxInput
              color='text-[#197D55]'
              className='checked:bg-[#F3F8F6] border-[#A3CBBB] bg-[#F3F8F6]'
              name="wholesale"
              id="wholesale"
              checked={formData.isWholesale}
              disabled={notAllowSale}
              onChange={({target}) => {
                if (notAllowSale) return;
                setFormData({...formData, isWholesale: target?.value});
                setErrors(prev => ({...prev, saleType: ''}));
              }}
            />
            <label className={cn(notAllowSale && 'cursor-not-allowed')} htmlFor="wholesale">Dành cho bán sỉ</label>
          </div>
          {formData.isWholesale && <div className='pb-5'>
            <div>
              {
                notAllowSale && (
                  <IconText
                    className='w-fit text-yellow-500 mb-2'
                    text='Ngành hàng này không hỗ trợ nhập giá và số lượng!'
                  />
                )
              }
              <p>Khoảng giá</p>
              <div className='flex flex-col md:flex-row gap-2.5 justify-between mt-1'>
                <Input
                  required
                  placeholder='Từ'
                  className='text-sm md:w-[308px] bg-white'
                  id="minPrice"
                  name="minPrice"
                  dot
                  value={formatNumberWithDots(formData.minPrice)}
                  onChange={handleInputChange}
                  error={errors.minPrice}
                  readOnly={notAllowSale}
                  setError={setErrors}
                />
                <Input
                  required
                  placeholder='Đến'
                  className='text-sm md:w-[308px] bg-white'
                  id="maxPrice"
                  name="maxPrice"
                  dot
                  value={formatNumberWithDots(formData.maxPrice)}
                  onChange={handleInputChange}
                  error={errors.maxPrice}
                  readOnly={notAllowSale}
                  setError={setErrors}
                />
              </div>
            </div>
            <div className='text-sm mt-5'>
              <p>Số lượng đặt hàng ít nhất</p>
              <div className='flex flex-col md:flex-row gap-2.5 justify-between mt-1'>
                <Input
                  classNameInput='bg-white'
                  required
                  placeholder='0'
                  dot
                  className='text-sm md:w-[308px] bg-white'
                  id="minOrderQty"
                  name="minOrderQty"
                  value={formatNumberWithDots(formData.minOrderQty)}
                  onChange={handleInputChange}
                  error={errors.minOrderQty}
                  readOnly={notAllowSale}
                  setError={setErrors}
                />
                <Input
                  required
                  placeholder='Đơn vị'
                  className='text-sm md:w-[308px] bg-white'
                  id="unitOfMeasure"
                  name="unitOfMeasure"
                  value={formData.unitOfMeasure}
                  onChange={handleInputChange}
                  error={errors.unitOfMeasure}
                  readOnly={notAllowSale}
                  setError={setErrors}
                />
              </div>
            </div>
          </div>}
        </div>
      </div>
      <div className='product-section'>
        <div id='images'>
          <p className='text-sm mb-2'>Hình sản phẩm</p>
          <ImageUpload
            onUpload={handleImageUpload}
            images={images}
            thumbIndex={thumbIndex}
            setThumbnailIndex={setThumbnailIndex}
          />
          {errors.images && (
            <div className='text-xs text-[#FF0019] mt-2 flex items-center gap-1'>
              <img src={IMG.icon.errorOutline} alt="errorOutline"/>
              <p className='text-[#E4626F] text-xs'>{errors.images}</p>
            </div>
          )}
        </div>
      </div>
      <div className='store-section'>
        <p className='text-sm'>Mô tả chi tiết</p>
        <Editor
          value={htmlString}
          handleChange={handleChangeEditor}
        />
      </div>

      <div className='w-full md:mb-8 md:mt-5'>
        <Button type='submit' className='mx-auto w-[320px]' variant={VARIANT.PRIMARY}
                onClick={handleSubmit}>
          {buttonSubmit}
        </Button>
      </div>
    </form>
  );
};

export default ProductForm;
